import type { CSSProperties } from 'react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDemo2Ctx } from '../Demo2Context';
import { STATUS_LABEL, STATUS_COLOR, STATUS_BG, MONITOR_LABEL, MONITOR_ICON_RI, moistStatus, treeIconRi, feedLog, lastFeedTime, type Zone } from '../demo2Data';
import './Demo2Moisture.css';

/* ═══════════════════════════════════════════════════════════
 * 전체 구역 평균 수분율 추이 — 임계 구간 밴드 + 부드러운 곡선 +
 * 오늘 지점 콜아웃을 갖춘 프리미엄 트렌드 차트.
 * 통계 카드(현재평균/최고/최저/변화)와 그래프가 서로 다른 값을
 * 보여주지 않도록, 계열(series)은 부모에서 한 번만 생성해 그대로 전달한다.
 * ═══════════════════════════════════════════════════════════ */
export function genTrendSeries(days: number, base: number): number[] {
    const pts: number[] = [];
    for (let i = 0; i < days; i++) {
        const wave = Math.sin(i * 0.5) * 5 + Math.sin(i * 1.15) * 2.6;
        pts.push(Math.max(12, Math.min(94, Math.round(base + (i - days / 2) * 0.22 + wave))));
    }
    pts[pts.length - 1] = base; // 마지막(오늘) 지점은 반드시 실제 현재 평균과 일치시킨다
    return pts;
}

function smoothPath(points: { x: number; y: number }[]): string {
    if (points.length < 2) return '';
    let d = `M ${points[0].x.toFixed(1)},${points[0].y.toFixed(1)}`;
    for (let i = 0; i < points.length - 1; i++) {
        const p0 = points[i], p1 = points[i + 1];
        const mx = (p0.x + p1.x) / 2, my = (p0.y + p1.y) / 2;
        d += ` Q ${p0.x.toFixed(1)},${p0.y.toFixed(1)} ${mx.toFixed(1)},${my.toFixed(1)}`;
    }
    const last = points[points.length - 1];
    d += ` L ${last.x.toFixed(1)},${last.y.toFixed(1)}`;
    return d;
}

function TrendChart({ series }: { series: number[] }) {
    const days = series.length;
    /* 라인/영역/밴드(SVG)는 preserveAspectRatio="none"으로 컨테이너 폭 100%를 그대로 채우고,
     * 축 라벨·콜아웃은 SVG 밖의 일반 HTML로 절대좌표(%) 배치해 폰트 크기가 항상 고정 px를
     * 유지하도록 분리한다 (SVG 확대/축소의 영향을 받지 않음). */
    const VB = 1000;
    const px = (i: number) => (i / (days - 1)) * VB;
    const py = (v: number) => (1 - v / 100) * VB;

    const coords = series.map((v, i) => ({ x: px(i), y: py(v) }));
    const linePath = smoothPath(coords);
    const areaPath = `${linePath} L${coords[coords.length - 1].x.toFixed(1)},${VB} L${coords[0].x.toFixed(1)},${VB} Z`;

    const bands = [
        { from: 0, to: 25, color: '#DC2626', op: 0.07 },
        { from: 25, to: 45, color: '#D97706', op: 0.07 },
        { from: 45, to: 70, color: '#2563EB', op: 0.06 },
        { from: 70, to: 100, color: '#1E8B53', op: 0.07 },
    ];

    const xLabelIdx = days === 14 ? [0, 6, 13] : [0, 7, 14, 21, 29];

    const last = series[series.length - 1];
    const lastColor = STATUS_COLOR[moistStatus(last)];
    const lastYPct = (py(last) / VB) * 100;

    return (
        <div className="moist2-chart-box">
            <div className="moist2-axis-y">
                {[100, 70, 45, 25, 0].map(v => (
                    <span key={v} style={{ top: `${(py(v) / VB) * 100}%` }}>{v}</span>
                ))}
            </div>
            <div className="moist2-plot-area">
                <svg viewBox={`0 0 ${VB} ${VB}`} preserveAspectRatio="none" style={{ width: '100%', height: '100%', display: 'block' }}>
                    <defs>
                        <linearGradient id="moist2AreaGrad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor={lastColor} stopOpacity="0.30" />
                            <stop offset="100%" stopColor={lastColor} stopOpacity="0.02" />
                        </linearGradient>
                    </defs>
                    {bands.map(b => (
                        <rect key={b.from} x={0} y={py(b.to)} width={VB} height={py(b.from) - py(b.to)} fill={b.color} opacity={b.op} />
                    ))}
                    {[25, 45, 70].map(v => (
                        <line key={v} x1={0} y1={py(v)} x2={VB} y2={py(v)} stroke="#94A3B8" strokeWidth={1.2} strokeDasharray="6 8" opacity={0.35} vectorEffect="non-scaling-stroke" />
                    ))}
                    <path d={areaPath} fill="url(#moist2AreaGrad)" />
                    <path d={linePath} fill="none" stroke={lastColor} strokeWidth={2.6} strokeLinecap="round" vectorEffect="non-scaling-stroke" />
                </svg>
                <div className="moist2-today-dot" style={{ top: `${lastYPct}%`, background: lastColor }} />
                <div className="moist2-today-callout" style={{ top: `${lastYPct}%` }}>오늘 {last}%</div>
            </div>
            <div className="moist2-axis-x">
                {xLabelIdx.map(i => (
                    <span key={i} className={i === 0 ? 'al' : i === days - 1 ? 'ar' : 'ac'} style={{ left: `${(i / (days - 1)) * 100}%` }}>
                        {i === days - 1 ? '오늘' : `${days - 1 - i}일 전`}
                    </span>
                ))}
            </div>
        </div>
    );
}

function ZoneGauge({ z }: { z: Zone }) {
    const c = STATUS_COLOR[z.status];
    const r = 27, circumference = 2 * Math.PI * r;
    const offset = circumference - (z.moist / 100) * circumference;
    return (
        <div className="moist2-zc-gauge">
            <svg viewBox="0 0 64 64">
                <circle className="bg" cx={32} cy={32} r={r} />
                <circle className="fg" cx={32} cy={32} r={r} stroke={c} strokeDasharray={circumference} strokeDashoffset={offset} />
            </svg>
            <div className="moist2-zc-gauge-val">
                <div className="moist2-zc-gauge-num" style={{ color: c }}>{z.moist}</div>
                <div className="moist2-zc-gauge-pct">%</div>
            </div>
        </div>
    );
}

export default function Demo2Moisture() {
    const navigate = useNavigate();
    const { zones } = useDemo2Ctx();
    const [period, setPeriod] = useState<14 | 30>(14);

    const avg = Math.round(zones.reduce((s, z) => s + z.moist, 0) / zones.length);
    const avgStatus = moistStatus(avg);
    const goodRate = Math.round((zones.filter(z => z.status === 'good').length / zones.length) * 100);
    const urgentCnt = zones.filter(z => z.status === 'warn' || z.status === 'danger').length;
    const sensorCnt = zones.filter(z => z.monitorType === 'sensor').length;
    const sorted = [...zones].sort((a, b) => a.moist - b.moist);

    const trendSeries = useMemo(() => genTrendSeries(period, avg), [period, avg]);
    const trendMax = Math.max(...trendSeries);
    const trendMin = Math.min(...trendSeries);
    const trendChange = trendSeries[trendSeries.length - 1] - trendSeries[0];

    const KPI: { lbl: string; val: string; sub: string; ico: string; tone: string }[] = [
        { lbl: '전체 평균 수분율', val: `${avg}%`, sub: `${STATUS_LABEL[avgStatus]} 구간`, ico: 'ri-drop-line', tone: avgStatus === 'good' ? 'green' : avgStatus === 'caution' ? 'blue' : avgStatus === 'warn' ? 'orange' : 'red' },
        { lbl: '양호 구역 비율', val: `${goodRate}%`, sub: `${zones.filter(z => z.status === 'good').length}/${zones.length}개 구역`, ico: 'ri-checkbox-circle-line', tone: 'green' },
        { lbl: '위험·심각 구역', val: `${urgentCnt}개`, sub: '즉시 조치 검토', ico: 'ri-alarm-warning-line', tone: 'red' },
        { lbl: '센서 커버리지', val: `${sensorCnt}/${zones.length}`, sub: '실측 센서 운영 구역', ico: 'ri-radar-line', tone: 'blue' },
    ];

    return (
        <div className="moist2">
            <div className="moist2-hero">
                <div>
                    <div className="moist2-hero-ttl"><i className="ri-drop-line" /> 수분 모니터링</div>
                    <div className="moist2-hero-sub">센서 기반 실측값과 센서리스 AI 예측을 통합해 전 구역 수분 상태를 관리합니다</div>
                </div>
                <div className="moist2-hero-actions">
                    <span className="moist2-chip sensor"><i className="ri-radar-line" /> 센서기반 {sensorCnt}개</span>
                    <span className="moist2-chip sensorless"><i className="ri-brain-line" /> AI예측 {zones.length - sensorCnt}개</span>
                    <button className="moist2-refresh"><i className="ri-refresh-line" /> 새로고침</button>
                </div>
            </div>

            <div className="moist2-kpi-grid">
                {KPI.map((k, i) => (
                    <div key={i} className="moist2-kpi-card">
                        <div className="moist2-kpi-top">
                            <div className={`d2-ico d2-ico-md d2-ico-${k.tone}`}><i className={k.ico} /></div>
                            <span className="moist2-kpi-lbl">{k.lbl}</span>
                        </div>
                        <div className="moist2-kpi-val">{k.val}</div>
                        <div className="moist2-kpi-sub">{k.sub}</div>
                    </div>
                ))}
            </div>

            <div className="moist2-card">
                <div className="moist2-card-hd">
                    <div>
                        <div className="moist2-card-hd-ttl"><i className="ri-line-chart-line" /> 전체 구역 평균 수분율 추이</div>
                        <div className="moist2-card-hd-sub">양호·주의·위험·심각 임계 구간을 배경 밴드로 함께 표시</div>
                    </div>
                    <div className="moist2-seg">
                        <button className={period === 14 ? 'active' : ''} onClick={() => setPeriod(14)}>14일</button>
                        <button className={period === 30 ? 'active' : ''} onClick={() => setPeriod(30)}>30일</button>
                    </div>
                </div>
                <div className="moist2-trend-stats">
                    <div className="moist2-trend-stat"><span className="moist2-trend-stat-lbl">현재 평균</span><span className="moist2-trend-stat-val" style={{ color: STATUS_COLOR[avgStatus] }}>{avg}%</span></div>
                    <div className="moist2-trend-stat"><span className="moist2-trend-stat-lbl">기간 내 최고</span><span className="moist2-trend-stat-val">{trendMax}%</span></div>
                    <div className="moist2-trend-stat"><span className="moist2-trend-stat-lbl">기간 내 최저</span><span className="moist2-trend-stat-val">{trendMin}%</span></div>
                    <div className="moist2-trend-stat"><span className="moist2-trend-stat-lbl">기간 내 변화</span><span className="moist2-trend-stat-val" style={{ color: trendChange >= 0 ? 'var(--good)' : 'var(--danger)' }}>{trendChange >= 0 ? '+' : ''}{trendChange}%p</span></div>
                </div>
                <div className="moist2-trend-chart-wrap">
                    <TrendChart series={trendSeries} />
                </div>
                <div className="moist2-trend-legend">
                    <span><span className="sq" style={{ background: '#1E8B53' }} />양호 70%↑</span>
                    <span><span className="sq" style={{ background: '#2563EB' }} />주의 45~69%</span>
                    <span><span className="sq" style={{ background: '#D97706' }} />위험 25~44%</span>
                    <span><span className="sq" style={{ background: '#DC2626' }} />심각 25%↓</span>
                </div>
            </div>

            <div className="moist2-section-hd">
                구역별 실시간 수분 지수
                <span className="sub">{zones.length}개 구역 · 실시간 갱신</span>
            </div>
            <div className="moist2-zone-grid">
                {zones.map(z => (
                    <div key={z.id} className="moist2-zone-card" style={{ '--zc-color': STATUS_COLOR[z.status] } as CSSProperties} onClick={() => navigate('/demo2/web/map')}>
                        <div className="moist2-zc-top">
                            <div>
                                <div className="moist2-zc-name"><i className={treeIconRi(z.treeName)} /> {z.name}</div>
                                <div className="moist2-zc-district">{z.district}</div>
                            </div>
                            <span className={`moist2-zc-badge ${z.monitorType}`}><i className={MONITOR_ICON_RI[z.monitorType]} /> {MONITOR_LABEL[z.monitorType]}</span>
                        </div>
                        <div className="moist2-zc-body">
                            <ZoneGauge z={z} />
                            <div className="moist2-zc-meta">
                                <span className="moist2-zc-status-pill" style={{ background: STATUS_BG[z.status], color: STATUS_COLOR[z.status] }}>{STATUS_LABEL[z.status]}</span>
                                <div className="moist2-zc-meta-row"><span className="moist2-zc-meta-lbl">마지막 관개</span><span className="moist2-zc-meta-val">{z.lastDate}</span></div>
                                <div className="moist2-zc-meta-row"><span className="moist2-zc-meta-lbl">담당자</span><span className="moist2-zc-meta-val">{z.mgr}</span></div>
                            </div>
                        </div>
                        <div className="moist2-zc-feed">
                            <div className="moist2-zc-feed-hd">
                                <span>{z.monitorType === 'sensor' ? '최근 센서 수신' : '최근 AI 예측 갱신'}</span>
                                <span className="moist2-zc-feed-time">{lastFeedTime(z)}</span>
                            </div>
                            <div className="moist2-zc-feed-strip">
                                {feedLog(z).map(d => (
                                    <span
                                        key={d.day}
                                        className={`moist2-zc-feed-dot${d.ok ? '' : ' miss'}`}
                                        title={`${d.day} · ${d.ok ? (z.monitorType === 'sensor' ? '센서 수신 완료' : 'AI 예측 갱신 완료') : '수신 실패'}`}
                                    />
                                ))}
                            </div>
                        </div>
                        <div className="moist2-zc-online">
                            <span>{z.category}</span>
                            <span className="live">{z.monitorType === 'sensor' ? '실시간 연동' : '매일 자동 예측'}</span>
                        </div>
                    </div>
                ))}
            </div>

            <div className="moist2-card" style={{ marginBottom: 24 }}>
                <div className="moist2-card-hd">
                    <div className="moist2-card-hd-ttl"><i className="ri-error-warning-line" /> 위험 지역 Top {Math.min(10, sorted.length)}</div>
                    <span className="text-xs" style={{ color: 'var(--d-ink3)', fontWeight: 600 }}>수분율 낮은 순</span>
                </div>
                <div className="moist2-risk-list">
                    {sorted.slice(0, 10).map((z, i) => (
                        <div key={z.id} className="moist2-risk-row" onClick={() => navigate('/demo2/web/map')}>
                            <div className={`moist2-risk-rank ${i < 3 ? 'top' : 'normal'}`}>{i + 1}</div>
                            <div className="moist2-risk-info">
                                <div className="moist2-risk-name"><i className={treeIconRi(z.treeName)} /> {z.name}</div>
                                <div className="moist2-risk-sub">{z.category} · {z.district}</div>
                            </div>
                            <div className="moist2-risk-bar"><div className="moist2-risk-bar-fill" style={{ width: `${z.moist}%`, background: STATUS_COLOR[z.status] }} /></div>
                            <div className="moist2-risk-val" style={{ color: STATUS_COLOR[z.status] }}>{z.moist}%</div>
                            <div className="moist2-risk-pill" style={{ background: STATUS_BG[z.status], color: STATUS_COLOR[z.status] }}>{STATUS_LABEL[z.status]}</div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
